/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  Autocomplete,
  Chip,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import Grid from "@mui/material/Grid";
import { useForm, Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import moment from "moment";

import {
  HolidayApplicableGender,
  HolidayApplicableReligion,
  HolidayType
} from "../../../../../../constants/holiday";
import { RootState } from "../../../../../../store";
import useGetDepartments from "../../../../../../hooks/department";
import { IHoliday } from "../../../../../../interfaces/HolidayInterface";
import { usePostHoliday, usePatchHoliday } from "../../../../../../hooks/query/useHolidays";
import { bsFullDate } from "../../../../../../components/Calendar/functions/calendarFunctions";

interface HolidayFormModalProps {
  open: boolean;
  date: Date | null;
  holiday?: Partial<IHoliday> | null;
  onClose: () => void;
  onSuccess: () => void;
  onDelete?: (id: number) => void;
}

// eslint-disable-next-line import/prefer-default-export
export function HolidayFormModal({
  open,
  date,
  holiday,
  onClose,
  onSuccess,
  onDelete
}: HolidayFormModalProps): React.JSX.Element | null {
  const isEdit = !!holiday?.id;

  const departments = useGetDepartments();
  const { mutate: postHolidayMutation } = usePostHoliday();
  const { mutate: patchHolidayMutation } = usePatchHoliday();
  const rcId = useSelector((state: RootState) => state.userContext.resourceCentre?.id);

  const activeDepartments = useMemo(
    () => (departments || []).filter((d) => d.active),
    [departments]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<IHoliday>({
    defaultValues: {
      name: "",
      date: "",
      type: HolidayType.NATIONAL,
      applicableGender: HolidayApplicableGender.ALL,
      applicableReligion: HolidayApplicableReligion.ALL,
      applicableDepartments: [],
      description: "",
      resourceCentreId: rcId || 0
    }
  });

  useEffect(() => {
    if (open) {
      reset({
        name: holiday?.name || "",
        // eslint-disable-next-line no-nested-ternary
        date: holiday?.date
          ? moment(holiday.date).format("YYYY-MM-DD")
          : date
          ? moment(date).format("YYYY-MM-DD")
          : "",
        type: holiday?.type || HolidayType.NATIONAL,
        applicableGender: holiday?.applicableGender || HolidayApplicableGender.ALL,
        applicableReligion: holiday?.applicableReligion || HolidayApplicableReligion.ALL,
        applicableDepartments: holiday?.applicableDepartments || [],
        description: holiday?.description || "",
        resourceCentreId: rcId || 0
      });
    }
  }, [open, holiday, date, reset, rcId]);

  const onSubmit = async (data: IHoliday) => {
    if (!rcId) return;

    const payload: Partial<IHoliday> = {
      ...data,
      resourceCentreId: rcId
    };

    if (isEdit && holiday?.id) {
      patchHolidayMutation(
        { id: holiday.id, data: payload },
        {
          onSuccess: (response) => {
            if (response.success) {
              onSuccess();
              onClose();
            }
          }
        }
      );
    } else {
      postHolidayMutation(payload, {
        onSuccess: (response) => {
          if (response.success) {
            onSuccess();
            onClose();
          }
        }
      });
    }
  };

  if (!date && !holiday) return null;

  const displayDate = date || (holiday?.date ? new Date(holiday.date) : null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {isEdit ? "Edit Holiday" : "Add Holiday"}
        <Box>
          {isEdit && onDelete && holiday?.id && (
            <IconButton
              color="error"
              onClick={() => onDelete(holiday.id as number)}
              disabled={isSubmitting}
              sx={{ mr: 1 }}
            >
              <DeleteIcon />
            </IconButton>
          )}
          <IconButton onClick={onClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3, mt: 1 }}>
          {displayDate && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                bgcolor: "grey.50",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "grey.200"
              }}
            >
              <Box sx={{ fontSize: "0.875 r em", color: "text.secondary", mb: 0.5 }}>
                Selected Date
              </Box>
              <Box sx={{ fontWeight: 600 }}>
                {displayDate.toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}{" "}
                ({bsFullDate(displayDate)})
              </Box>
            </Box>
          )}

          <form id="holiday-modal-form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }} sx={{ display: "flex", gap: 2 }}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: "Name is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      variant="outlined"
                      slotProps={{ inputLabel: { shrink: true } }}
                      fullWidth
                      label="Holiday Name"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      autoFocus
                    />
                  )}
                />

                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label="Holiday Type"
                      variant="outlined"
                      slotProps={{ inputLabel: { shrink: true } }}
                    >
                      {Object.values(HolidayType).map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="applicableGender"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label="Applicable Gender"
                      variant="outlined"
                      slotProps={{ inputLabel: { shrink: true } }}
                    >
                      {Object.entries(HolidayApplicableGender).map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                          {key}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="applicableReligion"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label="Applicable Religion"
                      variant="outlined"
                      slotProps={{ inputLabel: { shrink: true } }}
                    >
                      {Object.values(HolidayApplicableReligion).map((r) => (
                        <MenuItem key={r} value={r}>
                          {r === HolidayApplicableReligion.ALL ? "All" : r}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Controller
                  name="applicableDepartments"
                  control={control}
                  render={({ field }) => {
                    const selectedDepartments = activeDepartments.filter((d) =>
                      (field.value || []).includes(d.id)
                    );

                    return (
                      <Autocomplete
                        multiple
                        options={activeDepartments}
                        value={selectedDepartments}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_, newValue) => {
                          field.onChange(newValue.map((d) => d.id));
                        }}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.id}
                              label={option.name}
                              size="small"
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Applicable Departments"
                            placeholder="Select departments"
                            variant="outlined"
                            slotProps={{ inputLabel: { shrink: true } }}
                          />
                        )}
                      />
                    );
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      variant="outlined"
                      slotProps={{ inputLabel: { shrink: true } }}
                      fullWidth
                      multiline
                      rows={3}
                      label="Description"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 1,
          backgroundColor: "#ededed",
          display: "flex",
          justifyContent: "space-between",
          borderTop: 1,
          borderColor: "divider"
        }}
      >
        <Box />
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center"
          }}
        >
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            type="submit"
            variant="contained"
            form="holiday-modal-form"
            disabled={isSubmitting}
          >
            {isEdit ? "Update Holiday" : "Add Holiday"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
